import type { KeyStorage, ProviderId, Settings } from '../types';

const SETTINGS_KEY = 'cue.settings';
const KEYS_KEY = 'cue.keys';

export const DEFAULT_SETTINGS: Settings = {
  provider: 'anthropic',
  models: {},
  systemPrompt: '',
  theme: 'system',
  keyStorage: 'local',
  effort: 'medium',
  suggestions: true,
};

function safeStorage(kind: KeyStorage): Storage | null {
  try {
    return kind === 'local' ? window.localStorage : window.sessionStorage;
  } catch {
    return null;
  }
}

export function loadSettings(): Settings {
  const raw = safeStorage('local')?.getItem(SETTINGS_KEY);
  if (!raw) return { ...DEFAULT_SETTINGS };
  try {
    const parsed = JSON.parse(raw) as Partial<Settings>;
    return { ...DEFAULT_SETTINGS, ...parsed, models: { ...parsed.models } };
  } catch {
    return { ...DEFAULT_SETTINGS };
  }
}

export function saveSettings(settings: Settings): void {
  safeStorage('local')?.setItem(SETTINGS_KEY, JSON.stringify(settings));
}

export type ApiKeys = Partial<Record<ProviderId, string>>;

/** API keys live apart from the rest of the settings so the user can choose session-only storage. */
export function loadKeys(): ApiKeys {
  for (const kind of ['session', 'local'] as const) {
    const raw = safeStorage(kind)?.getItem(KEYS_KEY);
    if (raw) {
      try {
        return JSON.parse(raw) as ApiKeys;
      } catch {
        // fall through to the other storage
      }
    }
  }
  return {};
}

export function saveKeys(keys: ApiKeys, storage: KeyStorage): void {
  const clean: ApiKeys = {};
  for (const [provider, key] of Object.entries(keys)) {
    if (key && key.trim()) clean[provider as ProviderId] = key.trim();
  }
  const target = safeStorage(storage);
  const other = safeStorage(storage === 'local' ? 'session' : 'local');
  other?.removeItem(KEYS_KEY);
  if (Object.keys(clean).length === 0) target?.removeItem(KEYS_KEY);
  else target?.setItem(KEYS_KEY, JSON.stringify(clean));
}

export function clearAllLocalData(): void {
  for (const kind of ['local', 'session'] as const) {
    const storage = safeStorage(kind);
    if (!storage) continue;
    for (const key of Object.keys(storage)) {
      if (key.startsWith('cue')) storage.removeItem(key);
    }
  }
}
